import { useCallback, useEffect, useRef, useState } from 'react';
import { analysisApi, promptApi, settingsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useSelectedBlueprint } from '../../hooks/useSelectedBlueprint';
import { AIProvider, AIProviderInfo, BrandSafetyPrompt } from '../../types';

export interface RelevanceEngineConfig {
    selectedPromptId: string;
    setSelectedPromptId: (id: string) => void;
    /** Clear the persisted blueprint and reset the selection to empty (e.g. the
     * stored blueprint was deleted/inaccessible). */
    onInvalidSelection: () => void;
    /** The selected blueprint, resolved by id (promptApi.getById) rather than
     * pulled from a bulk list — the picker itself searches server-side, so
     * this hook only ever needs to know about the one currently selected
     * blueprint (deepcontext-q2bz.3). */
    selectedPrompt: BrandSafetyPrompt | undefined;
    selectedProvider: AIProvider;
    setSelectedProvider: (p: AIProvider) => void;
    selectedModel: string;
    setSelectedModel: (m: string) => void;
    providers: AIProviderInfo[];
    maxUrlsNonAdmin: number;
    isAdmin: boolean;
}

export function useRelevanceEngineConfig(): RelevanceEngineConfig {
    const { user, impersonation } = useAuth();
    const { selectedCompanyId } = useCompany();
    const isAdmin = user?.role === 'admin' && !impersonation?.active;

    // Persisted blueprint id — shared across blueprint-aware pages and survives
    // reloads. Seed the local selection from it on mount; every change is
    // written back through `setStoredBlueprintId`.
    const [storedBlueprintId, setStoredBlueprintId, clearStoredBlueprint] = useSelectedBlueprint();
    const [selectedPromptId, setSelectedPromptIdState] = useState(() => storedBlueprintId);
    const [selectedPrompt, setSelectedPrompt] = useState<BrandSafetyPrompt | undefined>(undefined);
    const [providers, setProviders] = useState<AIProviderInfo[]>([]);
    const [selectedProvider, setSelectedProvider] = useState<AIProvider>('google');
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [maxUrlsNonAdmin, setMaxUrlsNonAdmin] = useState(100);
    const selectedPromptLoadedKey = useRef<string | null>(null);
    // The company-change effect resets the selection to empty.
    // Skip that reset on the very first run so the persisted seed survives mount.
    const didMountRef = useRef(false);

    // Write selection changes through to localStorage while keeping local state
    // authoritative for this page's data fetching.
    const setSelectedPromptId = useCallback((id: string) => {
        setSelectedPromptIdState(id);
        setStoredBlueprintId(id);
    }, [setStoredBlueprintId]);

    // When the persisted blueprint no longer resolves (deleted/inaccessible),
    // clear storage and reset the selection to the empty state.
    const onInvalidSelection = useCallback(() => {
        clearStoredBlueprint();
        setSelectedPromptIdState('');
    }, [clearStoredBlueprint]);

    useEffect(() => {
        loadProviders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (impersonation) loadProviders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impersonation]);

    useEffect(() => {
        // On mount, keep the persisted seed. On an actual company switch, reset
        // the selection (the prior blueprint may not belong to the new company)
        // and clear the persisted id so it doesn't leak across companies.
        if (didMountRef.current) {
            setSelectedPromptIdState('');
            clearStoredBlueprint();
        } else {
            didMountRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyId]);

    // Resolve the selected blueprint by id — the picker itself searches
    // server-side (promptApi.getAllPaginated), so this hook never pulls a
    // bulk list (that had a 1,000-row ceiling that hid older blueprints,
    // deepcontext-q2bz.3).
    useEffect(() => {
        if (!selectedPromptId) {
            setSelectedPrompt(undefined);
            selectedPromptLoadedKey.current = null;
            return;
        }
        const companyIdToUse =
            user?.role === 'eskimi_admin' || user?.role === 'adops'
                ? selectedCompanyId || undefined
                : undefined;
        const loadKey = `${selectedPromptId}|${companyIdToUse || 'none'}`;
        if (selectedPromptLoadedKey.current === loadKey) return;
        selectedPromptLoadedKey.current = loadKey;
        let cancelled = false;
        (async () => {
            try {
                const prompt = await promptApi.getById(selectedPromptId, companyIdToUse);
                if (!cancelled) setSelectedPrompt(prompt);
            } catch {
                if (!cancelled) {
                    selectedPromptLoadedKey.current = null;
                    onInvalidSelection();
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPromptId, selectedCompanyId]);

    const loadProviders = async () => {
        try {
            const providersData = await analysisApi.getProviders();
            setProviders(providersData);
            const settings = await settingsApi.getPublic();
            const defaultProvider =
                settings.find((s) => s.key_name === 'default_ai_provider')?.value || 'openai';
            const defaultModel =
                settings.find((s) => s.key_name === 'default_ai_model')?.value || 'gpt-4o-mini';
            setMaxUrlsNonAdmin(
                Number(settings.find((s) => s.key_name === 'max_urls_non_admin')?.value) || 100,
            );
            const providerInfo = providersData.find(
                (p) => p.provider === defaultProvider && p.available,
            );
            if (providerInfo) {
                setSelectedProvider(defaultProvider as AIProvider);
                setSelectedModel(
                    providerInfo.models.includes(defaultModel)
                        ? defaultModel
                        : providerInfo.models[0] || '',
                );
            }
        } catch {
            // Surface via blank dropdowns; non-fatal.
        }
    };

    const setProviderAndDefaultModel = (next: AIProvider) => {
        setSelectedProvider(next);
        const info = providers.find((pi) => pi.provider === next);
        if (info && info.models.length > 0) setSelectedModel(info.models[0]);
    };

    return {
        selectedPromptId,
        setSelectedPromptId,
        onInvalidSelection,
        selectedPrompt,
        selectedProvider,
        setSelectedProvider: setProviderAndDefaultModel,
        selectedModel,
        setSelectedModel,
        providers,
        maxUrlsNonAdmin,
        isAdmin,
    };
}
